import ErrorBoundary from "@Components/ErrorBoundary";
import FullPageLoader from "@Components/FullPageLoader";
import LoaderIcon from "@Icons/LoaderIcon";
import { loadPaymentSuccessData, fetchPayment } from "@Pages/BookForm/data/loadPaymentSuccessData";
import { updateTokenPaymentData } from "@Pages/BookForm/data/bookFormReducer";
import verifyPaymentCompletionStatus from "@Pages/Payment/utils/verifyPaymentCompletionStatus";
import { AppState } from "@Store/index";
import loadable from "@loadable/component";
import { useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import AmberCopilotWrapper from "@Components/Chatbot/AmberCopilotWrapper";
import wrapperHOC from "@Utils/wrapperHOC";
import { useHistory } from "hooks/useHistory";
import useTranslationHook from "hooks/useTranslationHook";
import isWeChatMiniProgramClient from "@Utils/isWeChatMiniProgramClient";
import styles from "../PaymentConfirm/PaymentConfirm.module.scss";

const NotFound = loadable(() => import("@Pages/NotFound"), {
  fallback: <FullPageLoader />,
});

const PaymentSuccess = loadable(() => import("./PaymentSuccessWrapper"), {
  fallback: <FullPageLoader />,
});

type VerificationStatus = "loading_data" | "verifying" | "verified" | "failed";

const redirectToPaymentFailure = (
  history: ReturnType<typeof useHistory>,
  payment: any,
  token: string,
) => {
  const cancelUrl = payment?.gateway_details?.cancel_url;
  if (cancelUrl && cancelUrl.includes("/payments")) {
    const slicedLink = cancelUrl.split("/payments")[1];
    history.replace(`/payments${slicedLink}`);
    return;
  }
  history.replace(`/payments/${token}?payment_state=failed`);
};

const SUCCESS_STATES = new Set(["success", "succeeded"]);

const isSuccessState = (state?: string | null) => Boolean(state && SUCCESS_STATES.has(state));

const PaymentComplete = (props: any) => {
  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslationHook();
  const isMiniProgramClient = isWeChatMiniProgramClient();
  const queryParams = new URLSearchParams(location.search);
  const paymentToken = queryParams.get("token") || "";
  const { paymentTokenData } = useSelector(
    ({ bookFormData }: AppState) => ({
      paymentTokenData: bookFormData.paymentTokenData,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>(() =>
    isMiniProgramClient ? "loading_data" : "verified",
  );
  const verificationStartedRef = useRef(false);

  useEffect(() => {
    dispatch(loadPaymentSuccessData(props));
  }, []);

  useEffect(() => {
    if (!isMiniProgramClient) return undefined;
    if (paymentTokenData.err || !paymentToken) return undefined;
    if (paymentTokenData.token !== paymentToken) return undefined;
    if (verificationStartedRef.current) return undefined;

    verificationStartedRef.current = true;
    let cancelled = false;

    const payment = paymentTokenData.data;
    const isWechatPayment = payment?.gateway === "wechat";
    const shouldVerifyWeChatPayment = isWechatPayment && isMiniProgramClient;

    if (!shouldVerifyWeChatPayment) {
      setVerificationStatus("verified");
      return undefined;
    }

    if (isSuccessState(payment?.state)) {
      setVerificationStatus("verified");
      return undefined;
    }

    const verifyAndRefresh = async () => {
      setVerificationStatus("verifying");

      /* eslint-disable no-await-in-loop */
      while (!cancelled) {
        const result = await verifyPaymentCompletionStatus(paymentToken, payment, {
          maxAttempts: 8,
          intervalMs: 2000,
        });

        if (cancelled) return;

        if (result === "success") {
          if (payment?.state !== "success") {
            const freshPayment = await fetchPayment(paymentToken);
            if (!cancelled && freshPayment && !freshPayment.err) {
              dispatch(
                updateTokenPaymentData({
                  data: freshPayment,
                  token: paymentToken,
                  err: "",
                }),
              );
            }
          }
          setVerificationStatus("verified");
          return;
        }

        if (result === "failed") {
          setVerificationStatus("failed");
          redirectToPaymentFailure(history, payment, paymentToken);
          return;
        }
      }
      /* eslint-enable no-await-in-loop */
    };

    verifyAndRefresh();

    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    history,
    isMiniProgramClient,
    paymentToken,
    paymentTokenData.data,
    paymentTokenData.err,
    paymentTokenData.token,
  ]);

  if (paymentTokenData.err) {
    return <NotFound {...props} />;
  }

  if (paymentTokenData.token !== paymentToken) {
    return <FullPageLoader />;
  }

  // Web / non–mini program: never show the WeChat confirming-payment screen.
  if (!isMiniProgramClient) {
    return (
      <ErrorBoundary>
        <PaymentSuccess {...props} />
        <AmberCopilotWrapper />
      </ErrorBoundary>
    );
  }

  if (
    isMiniProgramClient &&
    (verificationStatus === "loading_data" || verificationStatus === "verifying")
  ) {
    return (
      <div className={styles.wrapper}>
        <LoaderIcon />
        <span>{t("paymentstrings.paymentconfirm.confirmingpayment")}</span>
      </div>
    );
  }

  if (verificationStatus === "failed") {
    return <FullPageLoader />;
  }

  return (
    <ErrorBoundary>
      <PaymentSuccess {...props} />
      <AmberCopilotWrapper />
    </ErrorBoundary>
  );
};

export default wrapperHOC(PaymentComplete, {
  componentName: "PaymentComplete-PaymentSuccess",
  showForChina: true,
});
